export function debounce(function_, wait, options) {
    let lastArguments
    let lastThis
    let maxWait
    let result
    let timerId
    let lastCallTime
    let lastInvokeTime = 0
    let leading = false
    let maxing = false
    let trailing = true

    // Bypass `requestAnimationFrame` by explicitly setting `wait=0`.
    const useRAF = !wait && wait !== 0 && typeof globalThis.requestAnimationFrame === 'function'

    if (typeof function_ !== 'function') {
        throw new TypeError('Expected a function')
    }
    wait = +wait || 0
    if (options) {
        leading = !!options.leading
        maxing = 'maxWait' in options
        maxWait = maxing ? Math.max(+options.maxWait || 0, wait) : maxWait
        trailing = 'trailing' in options ? !!options.trailing : trailing
    }

    function invokeFunction(time) {
        const arguments_ = lastArguments
        const thisArgument = lastThis

        lastArguments = lastThis = undefined
        lastInvokeTime = time
        result = function_.apply(thisArgument, arguments_)
        return result
    }

    function startTimer(pendingFunction, milliseconds) {
        if (useRAF) {
            globalThis.cancelAnimationFrame(timerId)
            return globalThis.requestAnimationFrame(pendingFunction)
        }
        // eslint-disable-next-line @typescript-eslint/no-implied-eval
        return setTimeout(pendingFunction, milliseconds)
    }

    function cancelTimer(id) {
        if (useRAF) {
            globalThis.cancelAnimationFrame(id)
            return
        }
        clearTimeout(id)
    }

    function leadingEdge(time) {
        // Reset any `maxWait` timer.
        lastInvokeTime = time
        // Start the timer for the trailing edge.
        timerId = startTimer(timerExpired, wait)
        // Invoke the leading edge.
        return leading ? invokeFunction(time) : result
    }

    function remainingWait(time) {
        const timeSinceLastCall = time - lastCallTime
        const timeSinceLastInvoke = time - lastInvokeTime
        const timeWaiting = wait - timeSinceLastCall

        return maxing ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke) : timeWaiting
    }

    function shouldInvoke(time) {
        const timeSinceLastCall = time - lastCallTime
        const timeSinceLastInvoke = time - lastInvokeTime

        // Either this is the first call, activity has stopped and we're at the
        // trailing edge, the system time has gone backwards and we're treating
        // it as the trailing edge, or we've hit the `maxWait` limit.
        return (
            lastCallTime === undefined ||
            timeSinceLastCall >= wait ||
            timeSinceLastCall < 0 ||
            (maxing && timeSinceLastInvoke >= maxWait)
        )
    }

    function timerExpired() {
        const time = Date.now()
        if (shouldInvoke(time)) {
            return trailingEdge(time)
        }
        // Restart the timer.
        timerId = startTimer(timerExpired, remainingWait(time))
        return
    }

    function trailingEdge(time) {
        timerId = undefined

        // Only invoke if we have `lastArgs` which means `func` has been
        // debounced at least once.
        if (trailing && lastArguments) {
            return invokeFunction(time)
        }
        lastArguments = lastThis = undefined
        return result
    }

    function cancel() {
        if (timerId !== undefined) {
            cancelTimer(timerId)
        }
        lastInvokeTime = 0
        lastArguments = lastCallTime = lastThis = timerId = undefined
    }

    function flush() {
        return timerId === undefined ? result : trailingEdge(Date.now())
    }

    function pending() {
        return timerId !== undefined
    }

    function debounced(...arguments_) {
        const time = Date.now()
        const isInvoking = shouldInvoke(time)

        lastArguments = arguments_
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        lastThis = this
        lastCallTime = time

        if (isInvoking) {
            if (timerId === undefined) {
                return leadingEdge(lastCallTime)
            }
            if (maxing) {
                // Handle invocations in a tight loop.
                timerId = startTimer(timerExpired, wait)
                return invokeFunction(lastCallTime)
            }
        }
        if (timerId === undefined) {
            timerId = startTimer(timerExpired, wait)
        }
        return result
    }
    debounced.cancel = cancel
    debounced.flush = flush
    debounced.pending = pending
    return debounced
}

export function throttle(function_, wait, options) {
    let leading = true
    let trailing = true

    if (typeof function_ !== 'function') {
        throw new TypeError('Expected a function')
    }
    if (options) {
        leading = 'leading' in options ? !!options.leading : leading
        trailing = 'trailing' in options ? !!options.trailing : trailing
    }
    return debounce(function_, wait, {
        leading,
        trailing,
        maxWait: wait,
    })
}
